import axios from '@/utils/axios'

import { defineStore } from 'pinia'
import { useAuthStore } from './auth'
import { useNotificationStore } from './notification'
import { getSavedState, saveState } from '@/utils/helper'
import { useFlowStore } from '@/stores/flow'

export const useDonationStore = defineStore('donation', {
  state: () => ({
    donationFlow: getSavedState('donation.Flow'),
    config: getSavedState('donation.Config'),
    processingFeesAndBoosts: null,
    boostPercentage: null
  }),
  getters: {
    getDonationFlow(state) {
      return state.donationFlow
    },
    getConfig(state) {
      return state.config
    },
    getFeesAndBoosts(state) {
      return state.processingFeesAndBoosts
    },
    getBoostPercentage(state) {
      return state.boostPercentage
    }
  },
  actions: {
    setBoostPercentage(percentage) {
      if (percentage === null || percentage === 0) {
        this.boostPercentage = percentage
        return
      }

      const allowedOptions =
        this.config?.donation?.boost_my_gift?.options?.map((o) => o.percentage) || []

      if (allowedOptions.length > 0 && !allowedOptions.includes(percentage)) {
        console.error('Invalid boost percentage:', percentage)
        return
      }
      this.boostPercentage = percentage
    },
    setConfig(config) {
      if (!config) {
        const flowStore = useFlowStore()
        flowStore.setFlowDone(false)
      }

      this.config = config
      saveState('donation.Config', config)
    },
    async fetchDonationFlow(charityId, flowId) {
      return axios
        .get(`/donate/${charityId}/flows/${flowId}`)
        .then((Response) => {
          this.donationFlow = Response.data.flow
          saveState('donation.Flow', Response.data.flow)
          return Response.data.flow
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(Error)
        })
    },
    async fetchDonationFees(
      amount,
      charity_id,
      type = 'one-time',
      payment_method = 'card',
      boost_rate
    ) {
      const timestamp = Math.round(new Date().getTime() / 1000 / 5) * 5
      if (type === 'monthly') {
        type = 'recurring'
      }

      if (type === 'guest') {
        type = 'one-time'
      }

      amount = Math.floor(amount)
      this.processingFeesAndBoosts = null
      return axios
        .get(`/donate/fee`, {
          params: {
            amount,
            charity_id,
            type,
            timestamp,
            payment_method,
            boost_rate
          }
        })
        .then((Response) => {
          this.processingFeesAndBoosts = Response.data
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(Error)
        })
    },
    async createDonationConfirmationCode(amount) {
      const authStore = useAuthStore()

      const formData = new URLSearchParams()
      formData.append('jwt', authStore.getToken)
      formData.append('amount_cents', amount)
      return axios
        .post(`/donate`, formData)
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(Error)
        })
    },
    async confirmDonation({
      confirmation_code,
      funding_source_id = false,
      campaign_id = 0,
      designation_id = 0,
      honorInfo = null,
      cover_fees = false,
      noteInfo = null,
      flow_id = null,
      custom_fields_response_token = '',
      company_id = null,
      enable_newsletter = false
    }) {
      const authStore = useAuthStore()

      let formFields = {}
      const formData = new FormData()
      formData.append('jwt', authStore.getToken)
      formData.append('confirmation_code', confirmation_code)
      formData.append('funding_source_id', funding_source_id)
      formData.append('enable_newsletter', enable_newsletter)

      if (company_id) {
        formData.append('company_id', company_id)
      }

      if (campaign_id) {
        formData.append('campaign_id', campaign_id)
      }

      if (designation_id) {
        formData.append('designation_id', designation_id)
      }

      if (cover_fees) {
        formData.append('cover_fees', cover_fees)
      }

      if (this.boostPercentage) {
        formData.append('boost', this.boostPercentage)
      }

      if (honorInfo && honorInfo.active) {
        formFields = {
          ...formFields,
          ...formatHonorRequestData(honorInfo)
        }
      }

      if (flow_id && Number(flow_id) !== 0) {
        formData.append('flow_id', flow_id)
      }

      if (custom_fields_response_token) {
        formData.append('custom_fields_response_token', custom_fields_response_token)
      }

      if (noteInfo && noteInfo.active) {
        formData.append('note[message]', noteInfo.noteRaw)
        formData.append('note[public]', noteInfo.public)
      }

      const payload = appendToFormData(formFields, formData)

      return axios
        .post(`/donate/confirm`, payload)
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(Error)
        })
    },
    async guestDonation({
      stripe_token,
      charity_id,
      amount,
      email,
      first_name,
      last_name,
      campaign_id = 0,
      designation_id = 0,
      honorInfo = null,
      cover_fees = false,
      noteInfo = null,
      recaptcha_token = '',
      flow_id = null,
      custom_fields_response_token = '',
      company_id = null,
      enable_newsletter = false
    }) {
      let formFields = {}
      const formData = new FormData()
      formData.append('stripe_token', stripe_token)
      formData.append('charity_id', charity_id)
      formData.append('amount', Math.round(amount * 100))
      formData.append('email', email)
      formData.append('first_name', first_name)
      formData.append('last_name', last_name)
      formData.append('recaptcha_token', recaptcha_token)
      formData.append('enable_newsletter', enable_newsletter)

      if (honorInfo && honorInfo.active) {
        formFields = {
          ...formFields,
          ...formatHonorRequestData(honorInfo)
        }
      }

      if (noteInfo && noteInfo.active) {
        formData.append('note[message]', noteInfo.noteRaw)
        formData.append('note[public]', noteInfo.public)
      }
      if (company_id) {
        formData.append('company_id', company_id)
      }
      if (cover_fees) {
        formData.append('cover_fees', cover_fees)
      }
      if (campaign_id) {
        formData.append('campaign_id', campaign_id)
      }
      if (designation_id) {
        formData.append('designation_id', designation_id)
      }
      if (flow_id && Number(flow_id) !== 0) {
        formData.append('flow_id', flow_id)
      }
      if (custom_fields_response_token) {
        formData.append('custom_fields_response_token', custom_fields_response_token)
      }

      const payload = appendToFormData(formFields, formData)

      return axios
        .post(`/donate/guest`, payload)
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(Error)
        })
    },
    async addRecurringDonation({
      funding_source_id,
      amount,
      campaign_id = 0,
      designation_id = 0,
      honorInfo = null,
      cover_fees = false,
      noteInfo = null,
      flow_id = null,
      custom_fields_response_token = '',
      company_id = null,
      enable_newsletter = false,
      token,
      setupIntentId = null
    }) {
      const authStore = useAuthStore()

      let formFields = {}
      const formData = new URLSearchParams()
      formData.append('amount', amount)
      formData.append('enable_newsletter', enable_newsletter)

      if (funding_source_id) {
        formData.append('funding_source_id', funding_source_id)
      }

      if (company_id) {
        formData.append('company_id', company_id)
      }
      if (campaign_id) {
        formData.append('campaign_id', campaign_id)
      }
      if (designation_id) {
        formData.append('designation_id', designation_id)
      }
      if (cover_fees) {
        formData.append('cover_fees', cover_fees)
      }
      if (honorInfo && honorInfo.active) {
        formFields = {
          ...formFields,
          ...formatHonorRequestData(honorInfo)
        }
      }
      if (flow_id && Number(flow_id) !== 0) {
        formData.append('flow_id', flow_id)
      }

      if (noteInfo && noteInfo.active) {
        formData.append('note[message]', noteInfo.noteRaw)
        formData.append('note[public]', noteInfo.public)
      }

      if (custom_fields_response_token) {
        formData.append('custom_fields_response_token', custom_fields_response_token)
      }

      if (setupIntentId) {
        formData.append('setup_intent_id', setupIntentId)
      }

      const jwToken = authStore.getToken || token

      const payload = appendToUrlSearchParams(formFields, formData)

      return axios
        .post(`/account/recurring`, payload, {
          headers: {
            'X-HARNESS-JWT': jwToken
          }
        })
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(Error)
        })
    },
    async submitCustomField({ flowId, params, charityId }) {
      return axios
        .post(`/donate/${charityId}/flows/${flowId}/custom-fields`, params)
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(notification.getNotification)
        })
    },
    async getAllCustomField({ flowId, charityId }) {
      return axios
        .get(`donate/${charityId}/flows/${flowId}/custom-fields`)
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          return Promise.reject(Error)
        })
    },
    async createDonationCart({
      recaptchaToken,
      charityId,
      donation,
      coverFees,
      boost,
      flowId,
      campaignId,
      designationId,
      honor,
      note,
      customFieldsResponseToken,
      companyId,
      enableNewsletter,
      guest
    }) {
      const authStore = useAuthStore()

      const userToken = authStore.getToken
      const isLoggedIn = !!userToken
      const honorInfo = createCartHonorData(honor)
      const noteAux = createCartNoteData(note)

      let headers = {}
      if (isLoggedIn) {
        headers['X-Harness-JWT'] = userToken
      }

      //TODO: change path to use our endpoint
      return axios
        .post(
          `/donate/${charityId}/cart`,
          {
            recaptchaToken,
            donation,
            coverFees,
            boost,
            flowId,
            campaignId,
            designationId,
            customFieldsResponseToken,
            companyId,
            enableNewsletter,
            note: noteAux,
            honor: honorInfo,
            guest: !isLoggedIn ? guest : null
          },
          {
            headers: headers
          }
        )
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(notification.getNotification)
        })
    },
    async updateDonationCart({
      // `cart` should be the full `cart` object returned within the `createDonationCart` response.
      cart,
      donation,
      coverFees,
      boost,
      flowId,
      campaignId,
      designationId,
      honor,
      note,
      customFieldsResponseToken,
      companyId,
      enableNewsletter,
      guest
    }) {
      const authStore = useAuthStore()

      const isLoggedIn = authStore.loggedIn
      const userToken = authStore.getToken
      const honorInfo = createCartHonorData(honor)
      const noteAux = createCartNoteData(note)

      let headers = {}
      if (authStore.loggedIn) {
        headers['X-Harness-JWT'] = userToken
      }

      return axios
        .put(
          `/donate/${cart.charityId}/cart/${cart.id}`,
          {
            donation,
            coverFees,
            boost,
            flowId,
            campaignId,
            designationId,
            customFieldsResponseToken,
            companyId,
            enableNewsletter,
            note: noteAux,
            honor: honorInfo,
            guest: !isLoggedIn ? guest : null
          },
          {
            headers: headers
          }
        )
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(notification.getNotification)
        })
    },
    async guestlessDonation({
      stripe_token,
      charityId,
      amount,
      email,
      firstName,
      lastName,
      campaign_id = 0,
      designation_id = 0,
      honorInfo = null,
      cover_fees = false,
      noteInfo = null,
      flow_id = null,
      custom_fields_response_token = '',
      company_id = null,
      newsletter = false,
      setupIntentId = null
    }) {
      let formFields = {}
      const formData = new FormData()
      formData.append('charity_id', charityId)
      formData.append('amount', Math.round(amount * 100))
      formData.append('email', email)
      formData.append('first_name', firstName)
      formData.append('last_name', lastName)
      formData.append('enable_newsletter', newsletter)

      if (setupIntentId) {
        formData.append('setup_intent_id', setupIntentId)
      }

      if (stripe_token) {
        formData.append('stripe_token', stripe_token)
      }

      if (honorInfo && honorInfo.active) {
        formFields = {
          ...formFields,
          ...formatHonorRequestData(honorInfo)
        }
      }

      if (noteInfo && noteInfo.active) {
        formData.append('note[message]', noteInfo.noteRaw)
        formData.append('note[public]', noteInfo.public)
      }
      if (company_id) {
        formData.append('company_id', company_id)
      }
      if (cover_fees) {
        formData.append('cover_fees', cover_fees)
      }

      if (this.boostPercentage) {
        formData.append('boost', this.boostPercentage)
      }

      if (campaign_id) {
        formData.append('campaign_id', campaign_id)
      }
      if (designation_id) {
        formData.append('designation_id', designation_id)
      }
      if (flow_id && Number(flow_id) !== 0) {
        formData.append('flow_id', flow_id)
      }
      if (custom_fields_response_token) {
        formData.append('custom_fields_response_token', custom_fields_response_token)
      }

      const payload = appendToFormData(formFields, formData)

      return axios
        .post(`/donate/one-time`, payload)
        .then((Response) => {
          return Response.data
        })
        .catch((Error) => {
          const notification = useNotificationStore()
          notification.getNotificationError(Error)
          return Promise.reject(Error)
        })
    }
  }
})

/**
 * Create an object containing the `honor` data to be sent when creating/modifying a Cart.
 * @param {object|null} honor The "In Honor/In Memory" data present on the donation Flow.
 * @returns {object|null} An object structured in the format expected by the Cart routes.
 */
const createCartHonorData = function (honor) {
  if (!honor) {
    return null
  }

  let honorInfo = {
    name: honor.recipient,
    from: honor.from,
    message: honor.noteRaw,
    method: honor.method,
    type: honor.type
  }

  if (honor.address) {
    honorInfo.address = honor.address
  }

  if (honor.email) {
    honorInfo.email = honor.email
  }

  return honorInfo
}

/**
 * Create an object containing the `note` data to be sent when creating/modifying a Cart.
 * @param {object|null} note The raw donation "Note" data present on the donation Flow.
 * @returns {object|null} An object structured in the format expected by the Cart routes.
 */
const createCartNoteData = function (note) {
  if (!note) {
    return null
  }

  return {
    message: note.noteRaw,
    public: note.public
  }
}

/**
 * Create an object containing the `honorInfo` user input that has the expected property names
 * for the new donation route APIs.
 * @param {object} honorInfo The object containing the "In Honor/In Memory" user input.
 */
const formatHonorRequestData = function (honorInfo) {
  const honorFields = {
    'honor[name]': honorInfo.recipient,
    'honor[from]': honorInfo.from,
    'honor[message]': honorInfo.noteRaw,
    'honor[email]': honorInfo.email,
    'honor[address]': honorInfo.address,
    'honor[method]': honorInfo.method,
    'honor[type]': honorInfo.type
  }

  // Remove any empty values, map the field names to `honor[xyz]` format, then return final object.
  //eslint-disable-next-line no-unused-vars
  return Object.fromEntries(Object.entries(honorFields).filter(([field, value]) => !!value))
}

/**
 * Creates a `FormData` object containing the data from the `formFields`.
 * Optionally, copies data from an `existingFormData` object into the new form object as well.
 * @param {object} formFields
 * @param {FormData|null} existingFormData
 * @returns {FormData}
 */
const appendToFormData = function (formFields, existingFormData = null) {
  const formData = new FormData()

  if (existingFormData) {
    for (const [existingKey, existingValue] of existingFormData) {
      formData.append(existingKey, existingValue)
    }
  }

  Object.entries(formFields).forEach(([field, value]) => {
    formData.append(field, value)
  })

  return formData
}

/**
 * Creates a `URLSearchParams` object containing the data from the `formFields`.
 * Optionally, copies data from an `existingFormData` object into the new form object as well.
 * @param {object} formFields
 * @param {URLSearchParams|null} existingFormData
 * @returns {URLSearchParams}
 */
const appendToUrlSearchParams = function (formFields, existingFormData = null) {
  const formData = new URLSearchParams()

  if (existingFormData) {
    for (const [existingKey, existingValue] of existingFormData) {
      formData.append(existingKey, existingValue)
    }
  }

  Object.entries(formFields).forEach(([field, value]) => {
    formData.append(field, value)
  })

  return formData
}
